// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, CheckCircle2, FolderOpen, X, Save, Sparkles, AlertTriangle, ChevronRight, Info } from 'lucide-react'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { BackButton }  from '@/components/ui/BackButton'
import { Button, Input, Textarea, Select } from '@/components/ui'
import { Card }         from '@/components/ui/Card'
import { EmptyState }   from '@/components/feedback/EmptyState'
import { LoadingState } from '@/components/feedback/LoadingState'
import { useAuthStore } from '@/store'
import { useToast }     from '@/hooks/useToast'
import { ROUTES }       from '@/lib/constants/routes'
import { navState }     from '@/lib/returnNav'
import { loadDocuments, saveDocument, updateDocument, deleteDocument, DOCUMENT_CATEGORIES, type DocumentEntry, loadMedicalWishes, loadDigitalEstate } from '@/services/wishes'
import { loadTrustedPersons } from '@/services/trustedPerson'
import {
  loadHousing, loadPension, loadInsurance, loadSecondHome,
  loadTestamentDetails, loadLivingWillDetails, wizardLoad, pensionFundNames,
} from '@/services/wizard'
import { loadNotarial } from '@/services/notarial'
import { loadVehicles } from '@/services/vehicles'

// ── Automatisch afgeleide documenten uit andere modules ─────────────────────────
// Read-only afgeleid bij render - GEEN opslag, GEEN datamodelwijziging.
// "location" leeg = WilsApp kent het document wel, maar nog geen locatie (aandachtspunt).
interface AutoDoc { label: string; location: string; source: string; route: string }

// ── Kerndocumenten-overzicht (read-only afgeleid) ───────────────────────────────
// Toont alleen of WilsApp het onderdeel uit uw gegevens herkent. Geen inhoudelijke
// beoordeling, geen opslag, geen datamodelwijziging.
interface KeyDoc { label: string; present: boolean; route: string }

async function deriveKeyDocs(userId: string): Promise<KeyDoc[]> {
  try {
    const [testament, livingWill, medical, trusted] = await Promise.all([
      loadTestamentDetails(userId), loadLivingWillDetails(userId),
      loadMedicalWishes(userId), loadTrustedPersons(userId),
    ])
    const m = medical
    const has = (v?: string) => !!(v && v.trim())
    return [
      { label: 'Testament', route: ROUTES.NOTARIAL,
        present: !!(testament && (testament.notarisName || testament.documentLocation || testament.year)) },
      { label: 'Levenstestament', route: ROUTES.NOTARIAL,
        present: !!(livingWill && (livingWill.notarisName || livingWill.documentLocation || livingWill.year)) || m?.livingWillExists === 'yes' },
      { label: 'Medische wensen vastgelegd', route: ROUTES.WISHES_MEDICAL,
        present: has(m?.treatmentPreferences) || has(m?.resuscitation) || has(m?.additionalWishes) },
      { label: 'Niet-reanimeren vastgelegd', route: ROUTES.WISHES_MEDICAL,
        present: has(m?.resuscitation) },
      { label: 'Medisch vertegenwoordiger', route: ROUTES.WISHES_MEDICAL,
        present: has(m?.proxyDecisionMaker) },
      { label: 'Vertrouwenspersoon', route: ROUTES.TRUSTED_PERSON,
        present: (trusted?.length ?? 0) > 0 },
      { label: 'Huisarts bekend', route: ROUTES.WISHES_MEDICAL,
        present: has(m?.gpName) },
    ]
  } catch {
    return []
  }
}

// ── Begeleidingsvragen (read-only afgeleid) ─────────────────────────────────────
// Neutrale vervolgvragen op basis van wat al is vastgelegd. Geen oordeel, geen
// medische of juridische conclusie - alleen een zetje om verder na te denken.
interface GuidanceItem { text: string; route: string }

async function deriveGuidance(userId: string): Promise<GuidanceItem[]> {
  try {
    const [testament, livingWill, medical, trusted, digital] = await Promise.all([
      loadTestamentDetails(userId), loadLivingWillDetails(userId),
      loadMedicalWishes(userId), loadTrustedPersons(userId), loadDigitalEstate(userId),
    ])
    const m = medical
    const has = (v?: string) => !!(v && v.trim())
    const hasLivingWill = !!(livingWill && (livingWill.notarisName || livingWill.documentLocation || livingWill.year)) || m?.livingWillExists === 'yes'
    const hasMedicalWishes = has(m?.treatmentPreferences) || has(m?.resuscitation) || has(m?.additionalWishes)
    const hasTestament = !!(testament && (testament.notarisName || testament.documentLocation || testament.year))
    const hasTrusted = (trusted?.length ?? 0) > 0
    const hasDigital = !!digital && Object.values(digital).some(v => typeof v === 'string' && v.trim())

    const items: GuidanceItem[] = []
    if (hasLivingWill && !hasMedicalWishes)
      items.push({ text: 'U heeft een levenstestament. Heeft u ook uw medische wensen vastgelegd?', route: ROUTES.WISHES_MEDICAL })
    if (hasMedicalWishes && !has(m?.gpName))
      items.push({ text: 'U heeft medische wensen vastgelegd. Weet uw huisarts hiervan?', route: ROUTES.WISHES_MEDICAL })
    if (hasTestament && !hasLivingWill)
      items.push({ text: 'U heeft een testament. Heeft u ook een levenstestament overwogen?', route: ROUTES.NOTARIAL })
    if (hasTrusted)
      items.push({ text: 'U heeft een vertrouwenspersoon vastgelegd. Weet deze persoon waar uw documenten staan?', route: ROUTES.DOCUMENTS })
    if (hasDigital)
      items.push({ text: 'U heeft digitale diensten ingevuld. Weet uw vertrouwenspersoon hoe deze later worden afgehandeld?', route: ROUTES.TRUSTED_PERSON })
    return items
  } catch {
    return []
  }
}

function nameLoc(notarisName?: string, notarisCity?: string): string {
  const n = (notarisName || '').trim()
  if (!n) return ''
  const c = (notarisCity || '').trim()
  return c ? `Bij notaris ${n}, ${c}` : `Bij notaris ${n}`
}

async function deriveAutoDocs(userId: string): Promise<AutoDoc[]> {
  const docs: AutoDoc[] = []
  try {
    const [testament, livingWill, notarial, housing, secondHome, insurance, pension, vehiclesData, fcRaw] =
      await Promise.all([
        loadTestamentDetails(userId), loadLivingWillDetails(userId), loadNotarial(userId),
        loadHousing(userId), loadSecondHome(userId), loadInsurance(userId), loadPension(userId),
        loadVehicles(userId), wizardLoad<Record<string, unknown>>(userId, 'funeralCosts'),
      ])

  // Testament
  if (testament && (testament.notarisName || testament.documentLocation || testament.year)) {
    docs.push({ label: 'Testament', source: 'Notariële wensen', route: ROUTES.NOTARIAL,
      location: (testament.documentLocation || '').trim() || nameLoc(testament.notarisName, testament.notarisCity) })
  }
  // Levenstestament
  if (livingWill && (livingWill.notarisName || livingWill.documentLocation || livingWill.year)) {
    docs.push({ label: 'Levenstestament', source: 'Notariële wensen', route: ROUTES.NOTARIAL,
      location: (livingWill.documentLocation || '').trim() || nameLoc(livingWill.notarisName, livingWill.notarisCity) })
  }
  // Huwelijkse voorwaarden
  if (notarial?.huwelijkseVoorwaarden === 'yes') {
    docs.push({ label: 'Huwelijkse voorwaarden', source: 'Notariële wensen', route: ROUTES.NOTARIAL,
      location: (notarial.huwelijkseVoorwaardenLocatie || '').trim() || nameLoc(notarial.huwelijkseVoorwaardenNotaris) })
  }
  // Uitvaartverzekering
  const fc = fcRaw as { hasInsurance?: string; insurer?: string; insurerOther?: string; policyNumber?: string; docsLocation?: string }
  if (fc?.hasInsurance === 'yes') {
    const ins = fc.insurer === 'Anders' ? (fc.insurerOther || '') : (fc.insurer || '')
    const bron = ins ? `${ins}${fc.policyNumber ? `, polis ${fc.policyNumber}` : ''}` : ''
    docs.push({ label: 'Uitvaartverzekering', source: 'Uitvaartkosten', route: ROUTES.WISHES_FUNERAL_COSTS,
      location: (fc.docsLocation || '').trim() || bron })
  }
  // Hypotheekstukken
  if (housing && (housing.mortgageNumber || housing.mortgageProvider)) {
    const bron = [housing.mortgageProvider, housing.mortgageNumber].filter(Boolean).join(', ')
    docs.push({ label: 'Hypotheekstukken', source: 'Financiën - woning', route: ROUTES.FINANCIAL,
      location: (housing.docsLocation || '').trim() || bron })
  }
  // Koopakte woning (woningdocumenten bekend zonder hypotheekgegevens)
  if (housing && (housing.docsLocation || '').trim() && !(housing.mortgageNumber || housing.mortgageProvider)) {
    docs.push({ label: 'Koopakte woning', source: 'Financiën - woning', route: ROUTES.FINANCIAL,
      location: (housing.docsLocation || '').trim() })
  }
  // Overlijdensrisicoverzekering
  if (housing?.hasOrv === 'yes') {
    docs.push({ label: 'Overlijdensrisicoverzekering', source: 'Financiën - woning', route: ROUTES.FINANCIAL,
      location: [housing.orvInsurer, housing.orvPolicyNr].filter(Boolean).join(', ') })
  }
  // Verzekeringspolissen
  const policies = insurance?.insurances ?? []
  if (policies.length) {
    let location = ''
    if (policies.length === 1) {
      const p = policies[0]
      const insurerName = p.insurer === 'Anders' ? (p.insurerOther || '') : (p.insurer || '')
      location = [insurerName, p.policyNumber].filter(Boolean).join(', ')
    } else {
      location = `${policies.length} polissen geregistreerd`
    }
    docs.push({ label: 'Verzekeringspolissen', source: 'Financiën - verzekeringen', route: ROUTES.FINANCIAL, location })
  }
  // Pensioeninformatie
  if (pension?.hasPension === 'yes') {
    const fund = pensionFundNames(pension).join(', ')
    docs.push({ label: 'Pensioeninformatie', source: 'Financiën - pensioen', route: ROUTES.FINANCIAL, location: fund.trim() })
  }
  // Buitenlandse woningdocumenten
  const hs = (secondHome?.hasSecondHome || '').trim()
  if (secondHome && hs && hs !== 'no' && (secondHome.address || secondHome.city || secondHome.street)) {
    const loc = (secondHome.documents?.ownership || '').trim() || [secondHome.city, secondHome.country].filter(Boolean).join(', ')
    docs.push({ label: 'Buitenlandse woningdocumenten', source: 'Financiën - tweede woning', route: ROUTES.FINANCIAL, location: loc })
  }
  // Voertuigdocumenten
  const vehicles = vehiclesData?.vehicles ?? []
  if (vehicles.length) {
    const v = vehicles[0]
    const plates = vehicles.map(x => x.kenteken).filter(Boolean).join(', ')
    const loc = (v.kentekencard || '').trim() || (plates ? `Kentekenbewijs ${plates}` : '')
    docs.push({ label: 'Voertuigdocumenten', source: 'Voertuigen', route: ROUTES.VEHICLES, location: loc })
  }
  } catch {
    // Afgeleide documenten zijn aanvullend; bij een onverwacht probleem niets tonen i.p.v. de pagina breken.
  }
  return docs
}

// Veelvoorkomende documenten voor de checklist "Ontbrekende documenten toevoegen".
const COMMON_DOCS: { label: string; category: string }[] = [
  { label: 'Paspoort',                 category: 'IDENTITY' },
  { label: 'Identiteitskaart',         category: 'IDENTITY' },
  { label: 'Testament',                category: 'LEGAL' },
  { label: 'Levenstestament',          category: 'LEGAL' },
  { label: 'Huwelijkse voorwaarden',   category: 'LEGAL' },
  { label: 'Uitvaartverzekering',      category: 'INSURANCE' },
  { label: 'Koopakte woning',          category: 'PROPERTY' },
  { label: 'Hypotheekstukken',         category: 'PROPERTY' },
  { label: 'Verzekeringspolissen',     category: 'INSURANCE' },
  { label: 'Pensioeninformatie',       category: 'FINANCIAL' },
  { label: 'Geboorteakten kinderen',   category: 'IDENTITY' },
  { label: 'Trouwboekje',              category: 'IDENTITY' },
  { label: "Diploma's",                category: 'OTHER' },
  { label: 'Contracten',               category: 'LEGAL' },
  { label: 'Overig document',          category: 'OTHER' },
]
const sameLabel = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

interface EntryForm { category: string; label: string; location: string; notes: string; isVerified: boolean }
const EMPTY_FORM: EntryForm = { category: '', label: '', location: '', notes: '', isVerified: false }

export function DocumentIndexPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { userId } = useAuthStore()
  const { toast }  = useToast()
  const navigate   = useNavigate()
  const [entries,   setEntries]   = useState<DocumentEntry[]>([])
  const [autoDocs,  setAutoDocs]  = useState<AutoDoc[]>([])
  const [keyDocs,   setKeyDocs]   = useState<KeyDoc[]>([])
  const [guidance,  setGuidance]  = useState<GuidanceItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm,  setShowForm]  = useState(false)
  const [editId,    setEditId]    = useState<string | null>(null)
  const [form,      setForm]      = useState<EntryForm>(EMPTY_FORM)
  const [isSaving,  setIsSaving]  = useState(false)
  const [errors,    setErrors]    = useState<Partial<EntryForm>>({})

  async function reload() {
    if (!userId) return
    try {
      const docs = await loadDocuments(userId)
      setEntries(docs)
      try { setAutoDocs(await deriveAutoDocs(userId)) } catch { setAutoDocs([]) }
      try { setKeyDocs(await deriveKeyDocs(userId)) } catch { setKeyDocs([]) }
      try { setGuidance(await deriveGuidance(userId)) } catch { setGuidance([]) }
    } catch {
      // Documenten konden niet geladen worden - toon de pagina i.p.v. een blanco/hangend scherm.
    } finally {
      setIsLoading(false)
    }
  }
  useEffect(() => { reload() }, [userId])

  // V49-UAT-02: scrollpositie behouden bij openen van een module en terugkeer
  const SCROLL_KEY = 'wilsapp_docwijzer_scroll'
  function goDoc(route: string) {
    try { sessionStorage.setItem(SCROLL_KEY, String(document.getElementById('main-content')?.scrollTop ?? 0)) } catch { /* noop */ }
    navigate(route, navState(ROUTES.DOCUMENTS, 'Documentwijzer'))
  }
  useEffect(() => {
    let raw: string | null = null
    try { raw = sessionStorage.getItem(SCROLL_KEY) } catch { raw = null }
    if (raw == null) return
    try { sessionStorage.removeItem(SCROLL_KEY) } catch { /* noop */ }
    const y = parseInt(raw, 10)
    if (Number.isNaN(y)) return
    const restore = () => { const el = document.getElementById('main-content'); if (el) el.scrollTop = y }
    requestAnimationFrame(restore)
    const t = setTimeout(restore, 150)   // herstel ook nadat afgeleide data de hoogte heeft aangepast
    return () => clearTimeout(t)
  }, [])

  function validate() {
    const e: Partial<EntryForm> = {}
    if (!form.category) e.category = 'Kies een categorie'
    if (!form.label.trim()) e.label = 'Voer een naam in'
    if (!form.location.trim()) e.location = 'Voer een locatie in'
    setErrors(e); return Object.keys(e).length === 0
  }

  function openNew() { setForm(EMPTY_FORM); setEditId(null); setErrors({}); setShowForm(true) }
  function openWith(label: string, category: string) {
    setForm({ ...EMPTY_FORM, label, category }); setEditId(null); setErrors({}); setShowForm(true)
  }
  function openEdit(e: DocumentEntry) {
    setForm({ category: e.category, label: e.label, location: e.location, notes: e.notes, isVerified: e.isVerified })
    setEditId(e.id); setErrors({}); setShowForm(true)
  }

  async function handleSave() {
    if (!userId || !validate()) return
    setIsSaving(true)
    try {
      if (editId) { await updateDocument(userId, editId, form); toast.success('Document bijgewerkt') }
      else { await saveDocument(userId, form); toast.success('Document toegevoegd') }
      setShowForm(false); setEditId(null); await reload()
    } catch { toast.error('Opslaan mislukt') }
    finally { setIsSaving(false) }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Weet u zeker dat u dit document wilt verwijderen?')) return
    await deleteDocument(id); toast.success('Verwijderd'); await reload()
  }

  const status = entries.length === 0 ? 'NOT_STARTED' : entries.length >= 3 ? 'COMPLETE' : 'IN_PROGRESS'
  const grouped = DOCUMENT_CATEGORIES.map(c => ({ ...c, items: entries.filter(e => e.category === c.value) })).filter(g => g.items.length > 0)

  // Voorkom dubbele invoer: een afgeleid document dat de gebruiker al handmatig vastlegde, niet nogmaals tonen.
  const visibleAutoDocs = autoDocs.filter(ad => !entries.some(e => sameLabel(e.label, ad.label)))
  // Checklist toont alleen documenten die nog niet bekend zijn (handmatig of automatisch).
  const knownLabels = [...entries.map(e => e.label), ...visibleAutoDocs.map(a => a.label)]
  const missingDocs = COMMON_DOCS.filter(d => !knownLabels.some(l => sameLabel(l, d.label)))

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      {!embedded && <BackButton />}
      <ModuleShell title="Documentwijzer" description="Leg vast waar uw belangrijke documenten zich bevinden - zoals 'Rode map in bureaulade' of 'Bij de notaris'. U slaat geen bestanden op, alleen de locatie. Leg dit vast nu u nog precies weet waar alles ligt."
      status={status as 'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'}
      actions={<Button size="sm" onClick={openNew} icon={<Plus className="h-4 w-4" />}>Locatie toevoegen</Button>}
    >
      {/* Kerndocumenten - overzicht (read-only afgeleid, geen oordeel) */}
      {keyDocs.length > 0 && (
        <Card>
          <p className="text-sm font-semibold text-ink mb-1">Kerndocumenten - overzicht</p>
          <p className="text-xs text-ink-4 mb-3 leading-relaxed">
            Dit toont welke onderdelen WilsApp uit uw gegevens herkent. WilsApp beoordeelt de inhoud niet; het geeft alleen overzicht.
          </p>
          <div className="flex flex-col divide-y divide-sand-200">
            {keyDocs.map(k => (
              <button
                key={k.label}
                type="button"
                onClick={() => goDoc(k.route)}
                className="flex items-center gap-2.5 py-2.5 text-left hover:opacity-80"
              >
                {k.present
                  ? <CheckCircle2 className="h-4 w-4 text-sage-600 flex-shrink-0" />
                  : <span className="h-4 w-4 flex-shrink-0 rounded-full border border-sand-300" aria-hidden="true" />}
                <span className="flex-1 text-sm text-ink">{k.label}</span>
                <span className={`text-xs ${k.present ? 'text-sage-600' : 'text-ink-4'}`}>
                  {k.present ? 'Aanwezig' : 'Nog niet vastgelegd'}
                </span>
                <ChevronRight className="h-4 w-4 text-ink-4 flex-shrink-0" />
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-l-4 border-status-infoBorder border-l-status-infoText bg-status-infoBg px-4 py-3.5 mt-3">
            <div className="flex items-start gap-3">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-status-infoText/10 text-status-infoText">
                <Info className="h-4 w-4" aria-hidden="true" />
              </span>
              <p className="text-[13px] text-ink-3 leading-relaxed">
                Een euthanasieverklaring en de gegevens van een behandelend specialist legt WilsApp niet apart vast.
                U bespreekt en regelt deze via uw{' '}
                <button type="button" onClick={() => goDoc(ROUTES.WISHES_MEDICAL)} className="text-sage-700 font-medium hover:underline">medische wensen</button>
                {' '}en uw huisarts. Algemene{' '}
                <button type="button" onClick={() => goDoc(ROUTES.EUTHANASIE_INFO)} className="text-sage-700 font-medium hover:underline">informatie over euthanasie</button>
                {' '}vindt u in WilsApp.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Vervolgvragen - neutrale begeleiding, read-only, geen oordeel */}
      {guidance.length > 0 && (
        <Card className="border-sage-200 bg-sage-50/40">
          <p className="text-sm font-semibold text-ink mb-1">Misschien wilt u hier nog naar kijken</p>
          <p className="text-xs text-ink-4 mb-3 leading-relaxed">
            Deze vragen zijn afgeleid uit wat u al heeft vastgelegd. WilsApp beoordeelt niets; het zijn alleen aandachtspunten.
          </p>
          <div className="flex flex-col gap-2">
            {guidance.map((g, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goDoc(g.route)}
                className="flex items-start gap-2.5 rounded-xl bg-moss text-white px-4 py-3 text-left shadow-soft transition-all duration-150 ease-soft hover:bg-moss-light active:bg-sage-900 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sage-600"
              >
                <span className="text-white/80 text-base leading-none flex-shrink-0" aria-hidden="true">?</span>
                <span className="flex-1 text-sm text-white">{g.text}</span>
                <ChevronRight className="h-4 w-4 text-white flex-shrink-0 mt-0.5" />
              </button>
            ))}
          </div>
        </Card>
      )}

      {showForm && (
        <Card className="border-sage-200 bg-sage-50/50">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-ink">{editId ? 'Locatie bewerken' : 'Locatie vastleggen'}</h2>
            <button onClick={() => setShowForm(false)} className="text-ink-4 hover:text-ink-3"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex flex-col gap-4">
            <p className="text-xs text-ink-4 leading-relaxed -mt-1">
              Vul hier in <strong>waar</strong> het document te vinden is - niet het document zelf.
              Bijv: 'Rode map in bureaulade', 'Bij notaris Van der Ploeg', 'Digitale kluis op computer'.
            </p>
            <Select label="Categorie" options={DOCUMENT_CATEGORIES} value={form.category}
              onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="Kies een categorie..." error={errors.category} />
            <Input label="Naam document" value={form.label}
              onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Bijv: Paspoort, Testament..." error={errors.label} required />
            <Input label="Locatie" value={form.location}
              onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Bijv: Rode map linkerla bureau, bij notaris..." error={errors.location} required />
            <Textarea label="Aanvullende info" value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Verloopdatum, polisnummer..." maxLength={300} />
            <div className="flex gap-3 pt-1">
              <Button fullWidth onClick={handleSave} loading={isSaving} icon={<Save className="h-4 w-4" />}>Opslaan</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Annuleren</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Automatisch gevonden uit uw dossier */}
      {visibleAutoDocs.length > 0 && !showForm && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
            <Sparkles className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-ink">Automatisch gevonden uit uw dossier</p>
              <p className="text-xs text-ink-3 leading-relaxed mt-0.5">
                WilsApp heeft al een deel voor u gevonden. Controleer of dit klopt en vul aan wat ontbreekt.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {visibleAutoDocs.map((ad, i) => {
              const missing = !ad.location.trim()
              return (
                <Card key={i} padding="sm" className={missing ? 'border-amber-200 bg-amber-50/40' : ''}>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {missing
                        ? <AlertTriangle className="h-4 w-4 text-amber-600" />
                        : <CheckCircle2 className="h-4 w-4 text-sage-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink">{ad.label}</p>
                      {missing ? (
                        <p className="text-xs text-amber-800 mt-0.5 leading-snug">
                          Bekend, maar locatie ontbreekt. U heeft dit aangegeven, maar nog niet waar het document te vinden is.
                        </p>
                      ) : (
                        <p className="text-xs text-ink-4 mt-0.5">{ad.location}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className="inline-flex items-center rounded-full bg-sand px-2 py-0.5 text-[11px] font-medium text-ink-3">{ad.source}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${missing ? 'bg-amber-100 text-amber-800' : 'bg-sage-100 text-sage-700'}`}>
                          {missing ? 'Locatie ontbreekt' : 'Automatisch overgenomen'}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => goDoc(ad.route)}
                      className="flex items-center gap-1 flex-shrink-0 self-center rounded-lg border border-sand-300 bg-white px-2.5 py-2 text-xs font-medium text-ink-3 hover:border-sage-300 hover:text-ink-2 transition-colors">
                      {missing ? 'Locatie toevoegen' : 'Controleren'}
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* Ontbrekende documenten toevoegen */}
      {missingDocs.length > 0 && !showForm && (
        <div className="flex flex-col gap-2.5">
          <p className="text-sm font-semibold text-ink">Ontbrekende documenten toevoegen</p>
          <p className="text-xs text-ink-3 -mt-1">Tik op een document om zelf de locatie vast te leggen.</p>
          <div className="flex flex-wrap gap-2">
            {missingDocs.map(d => (
              <button key={d.label} onClick={() => openWith(d.label, d.category)}
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-sand-300 bg-white px-3 py-2 text-xs font-medium text-ink-3 hover:border-sage-400 hover:text-ink-2 active:scale-[0.98] transition-all">
                <Plus className="h-3.5 w-3.5" /> {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {entries.length > 0 && visibleAutoDocs.length > 0 && !showForm && (
        <p className="text-xs font-medium text-ink-4 uppercase tracking-wider mt-1">Zelf vastgelegde locaties</p>
      )}

      {entries.length === 0 && visibleAutoDocs.length === 0 && missingDocs.length === 0 && !showForm && (
        <EmptyState icon={<FolderOpen className="h-8 w-8" />} title="Nog geen documenten toegevoegd"
          description="Voeg de locatie toe van uw paspoort, testament, verzekeringen en andere belangrijke documenten."
          action={{ label: 'Locatie van een document vastleggen', onClick: openNew }} />
      )}

      {grouped.map(group => (
        <div key={group.value}>
          <p className="text-xs font-medium text-ink-4 uppercase tracking-wider mb-2.5">{group.label}</p>
          <div className="flex flex-col gap-2">
            {group.items.map(entry => (
              <Card key={entry.id} padding="sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {entry.isVerified ? <CheckCircle2 className="h-4 w-4 text-sage-600" /> : <div className="h-4 w-4 rounded-full border-2 border-sand-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink">{entry.label}</p>
                    <p className="text-xs text-ink-4 mt-0.5">{entry.location}</p>
                    {entry.notes && <p className="text-xs text-ink-4 mt-1 italic">{entry.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(entry)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-sand-100 transition-colors" title="Bewerken"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => handleDelete(entry.id)} className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors" title="Verwijderen"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {entries.length > 0 && !showForm && (
        <Button variant="outline" onClick={openNew} icon={<Plus className="h-4 w-4" />}>Locatie toevoegen</Button>
      )}

      <div className="rounded-xl bg-sand px-4 py-3 text-xs text-ink-4 leading-relaxed">
        <strong className="text-ink-3">Let op:</strong> Sla hier alleen de locatie op - nooit de documenten zelf. WilsApp is geen documentkluis.
      </div>
          {!embedded && <BackButton variant="bottom" primaryLabel="Klaar - terug naar dashboard" />}
    </ModuleShell>
    </div>
  )
}